import React, { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { Button } from 'primereact/button';
import { useQuery, useMutation } from 'react-query';
import { TOAST } from 'constant';
import { toast } from 'layout';
import { createProposal, uploadProposalFile } from 'apis/proposals.api';
import { getAll as getAllResearchFields } from 'apis/researchFields.api';
import { catchError, toNonAccentVietnamese } from 'utils/func';
import { proposalValidationSchema } from '../../validation';
import ProposalForm from '../../components/ProposalForm/index';

const formOptions = { resolver: yupResolver(proposalValidationSchema) };

export default function CreateProposal() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const {
    setValue,
    control,
    handleSubmit,
    formState: { errors, isDirty, dirtyFields },
    watch,
    trigger,
  } = useForm({ ...formOptions, mode: 'onChange' });

  const { data: researchFieldsData } = useQuery(['proposalResearchFields'], getAllResearchFields);

  const researchFields = useMemo(
    () =>
      researchFieldsData?.data.map((e) => ({
        id: e.id,
        name: e.code ? t(`researchFields.${e.code}`) : e.name,
        code: e.code,
      })) ?? [],
    [researchFieldsData, t]
  );

  const { mutate: createProposalMutate } = useMutation(createProposal);

  const { mutate: uploadMutate } = useMutation(uploadProposalFile);

  const checkValidForm = async () => {
    const isValidTrigger = await trigger();

    if (!isValidTrigger) {
      toast(TOAST.ERROR, t('errorMessage.validationErrorMessage'));
    }
  };

  const onSubmit = async (formData) => {
    const { researchFieldIds, owner, ...restFormData } = formData;

    const finalData = {
      ...restFormData,
      ownerId: owner?.ownerId,
      proposalResearchFields: researchFieldIds.map((researchFieldId) => ({
        researchFieldId,
      })),
    };

    // First, create the proposal
    createProposalMutate(finalData, {
      onSuccess: (data) => {
        toast(TOAST.SUCCESS, t('proposal.create.successCreateMessage'));

        const formFileData = new FormData();
        formFileData.append('proposalId', data?.data?.id);
        // eslint-disable-next-line no-param-reassign
        const newFileName = `R01-DX-DA-${finalData.yearProposed}-${toNonAccentVietnamese(
          owner.fullname
        )}.pdf`;

        const renamedFile = new File([formData?.file], newFileName, {
          type: formData?.file.type,
        });

        formFileData.append('proposalFile', renamedFile);

        uploadMutate(
          { id: data?.data?.id, data: formFileData },
          {
            onSuccess: () => {
              toast(TOAST.SUCCESS, t('proposal.create.successUploadFile'));

              setTimeout(() => {
                navigate(`/proposals/edit/${data?.data?.id}`, {
                  replace: true,
                });
              }, 300);
            },
            onError: () => {
              toast(TOAST.ERROR, t('proposal.create.errorUploadFile'));
            },
          }
        );
      },

      onError: (data) => {
        if (data?.response?.data?.code === 'proposalAlreadyExists') {
          toast(TOAST.ERROR, t('proposal.create.proposalAlreadyExists'));
        }
        if (data?.response?.data?.code === 'proposalProgramNotInTimeRange') {
          toast(TOAST.ERROR, t('proposal.create.proposalProgramNotInTimeRange'));
        }

        catchError(t, data, navigate);
      },
    });
  };

  useEffect(() => {
    const currentYear = new Date().getFullYear();
    setValue('yearProposed', currentYear);
  }, []);

  return (
    <div className="card relative">
      <form onSubmit={handleSubmit(onSubmit)}>
        <ProposalForm
          researchFields={researchFields}
          control={control}
          watch={watch}
          errors={errors}
          setValue={setValue}
        />
        <div
          className="text-right mt-4"
          style={{ display: 'flex', justifyContent: 'flex-end', gap: '14px' }}
        >
          <Button
            label={t('proposal.create.cancel')}
            className="w-7rem"
            severity="danger"
            onClick={() => navigate('/proposals')}
          />
          <Button
            label={t('proposal.create.save')}
            className="w-7rem"
            severity="info"
            type="submit"
            onClick={checkValidForm}
            disabled={!isDirty || Object.keys(dirtyFields).length === 0}
          />
          <Button
            label={t('proposal.create.submit')}
            className="w-7rem"
            severity="success"
            disabled
          />
        </div>
      </form>
    </div>
  );
}
